// oxlint-disable-next-line no-restricted-imports -- Axios is confined to this transport boundary.
import axios, { type AxiosResponse } from "axios";
import { getApiResponseDiagnostics } from "./apiResponseDiagnostics";

export type HttpMethod =
  | "get"
  | "post"
  | "put"
  | "patch"
  | "delete"
  | "head"
  | "options"
  | "GET"
  | "POST"
  | "PUT"
  | "PATCH"
  | "DELETE"
  | "HEAD"
  | "OPTIONS";

export type HttpRequestOptions = {
  url: string;
  method: HttpMethod;
  params?: unknown;
  data?: unknown;
  headers?: Record<string, string | number | boolean | undefined>;
  timeout?: number;
  signal?: AbortSignal;
  responseType?: "json" | "text";
};

export type HttpResponse<T = unknown> = {
  data: T;
  status: number;
  headers: Record<string, unknown>;
  url: string;
};

export class HttpError<T = unknown> extends Error {
  readonly name = "HttpError";
  readonly code?: string;
  readonly response?: HttpResponse<T>;
  readonly reason?: "invalid-json";
  readonly diagnostics?: ReturnType<typeof getApiResponseDiagnostics>;

  constructor({
    message,
    code,
    response,
    reason,
    diagnostics,
  }: {
    message: string;
    code?: string;
    response?: HttpResponse<T>;
    reason?: "invalid-json";
    diagnostics?: ReturnType<typeof getApiResponseDiagnostics>;
  }) {
    super(message);
    this.code = code;
    this.response = response;
    this.reason = reason;
    this.diagnostics = diagnostics;
  }
}

export const isHttpError = <T = unknown>(
  error: unknown,
): error is HttpError<T> => error instanceof HttpError;

const toHttpResponse = <T>(response: AxiosResponse<T>): HttpResponse<T> => ({
  data: response.data,
  status: response.status,
  headers: { ...response.headers },
  url: response.config.url ?? "",
});

/** JSON APIs parse strictly; CSV callers must explicitly request text. */
export const httpRequest = async <T = unknown>(
  options: HttpRequestOptions,
): Promise<HttpResponse<T>> => {
  try {
    const response = await axios<T>({
      ...options,
      responseType: options.responseType ?? "json",
      transitional: { silentJSONParsing: false },
    });
    // Axios leaves empty bodies untouched, preserving existing 204/void callers.
    // Callers expecting a data envelope still validate that separately.
    return toHttpResponse(response);
  } catch (error) {
    if (!axios.isAxiosError<unknown>(error)) throw error;
    let response = error.response ? toHttpResponse(error.response) : undefined;
    let invalidJson =
      error.code === "ERR_BAD_RESPONSE" && error.cause instanceof SyntaxError;
    // Text downloads still receive structured JSON API errors on failure.
    if (
      options.responseType === "text" &&
      response &&
      typeof response.data === "string" &&
      response.data
    ) {
      const contentType = response.headers["content-type"];
      if (
        typeof contentType === "string" &&
        contentType.toLowerCase().includes("application/json")
      ) {
        try {
          response = {
            ...response,
            data: JSON.parse(response.data) as unknown,
          };
        } catch {
          invalidJson = true;
        }
      }
    }
    const diagnostics =
      invalidJson && response ? getApiResponseDiagnostics(response) : undefined;
    throw new HttpError({
      message: invalidJson ? "Invalid JSON response" : error.message,
      code: invalidJson ? "ERR_BAD_RESPONSE" : error.code,
      // Do not retain malformed body text, parser messages, or the Axios cause/config.
      response:
        invalidJson && response ? { ...response, data: undefined } : response,
      reason: invalidJson ? "invalid-json" : undefined,
      diagnostics,
    });
  }
};
